import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import {
  isThisTabImpersonating,
  removeImpersonationStorageItem,
  IMPERSONATION_TUNNEL_DATA_KEY,
  IMPERSONATION_USER_DATA_KEY,
} from '../shared/utils/impersonation-handoff.util';
import { isTunnelAppContext } from '../shared/utils/tunnel-app-context.util';
import { isFeedbackPublicPath } from '../feedback/feedback-branding.util';

/**
 * HTTP interceptor that manages authentication-related HTTP responses and redirects on token expiration.
 * It passes through successful requests but intercepts errors to handle 401 Unauthorized responses,
 * avoid redirect loops for public and authentication endpoints, and perform logout/redirect logic for
 * regular and super-admin users.
 * @component
 * @selector app-auth-interceptor
 * @example
 *   <app-auth-interceptor></app-auth-interceptor>
 * @input none none - This injectable does not expose any Angular @Input properties.
 * @output none none - This injectable does not emit any Angular @Output events.
 *
 * Purpose:
 * - Acts as an HttpInterceptor to monitor HTTP responses and errors across the application.
 * - Prevents automatic login redirect for a configurable list of public routes and for authentication endpoints.
 * - Performs cleanup of authentication data and redirects users to the appropriate login page when a token
 *   is invalid or expired.
 *
 * Key properties:
 * - publicRoutes: string[] - List of URL prefixes considered public; routes matching these will not trigger
 *   automatic logout/redirect on 401.
 *
 * Key methods:
 * - isPublicRoute(): boolean
 *   - Determines whether the current router url (stripped of query params and fragments) starts with any
 *     configured public route. Used to avoid redirecting users when they hit public pages.
 * - handleTokenExpiration(): void
 *   - Centralized logic to handle token expiry or unauthorized state. It checks if the current route
 *     is an admin route or if super-admin data exists in localStorage. For super-admins, it clears both
 *     superAdminData and userData, resets the authService user state, and navigates to /admin/login.
 *     For regular users, it clears userData, resets authService user state, and navigates to /login.
 * - intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>>
 *   - Implements the HttpInterceptor interface. For each request it:
 *     - Forwards the request and taps successful HttpResponse events (no-op logging placeholder).
 *     - Catches HttpErrorResponse errors and:
 *       - Detects authentication endpoints (login, forgot-password, reset-password) and excludes them
 *         from triggering logout on 401.
 *       - If a 401 status or an explicit "Invalid or expired token" message is encountered, and the
 *         request is not to an auth endpoint and the current route is not public, calls handleTokenExpiration().
 *       - Re-throws the original error after handling.
 *
 * Dependencies:
 * - Injects AuthService to update observable user state when logging out.
 * - Injects Router to inspect the current URL and perform navigation.
 *
 * Lifecycle hooks:
 * - None: This service is an application-wide singleton injectable (providedIn: 'root') and does not
 *   implement Angular component lifecycle hooks.
 *
 * Notes and important behaviors:
 * - The interceptor normalizes the router URL by stripping query parameters and fragment identifiers
 *   before matching against publicRoutes or detecting admin routes.
 * - Authentication endpoints are explicitly checked by substring matching against the request URL to
 *   avoid treating failed login attempts as token-expiration events.
 * - Token expiration handling includes removing relevant items from localStorage and resetting the
 *   in-memory user state via AuthService before redirecting to the appropriate login page.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  // List of public routes that should not trigger login redirect on 401.
  // Only truly public routes belong here. Protected routes with AuthGuard
  // must NOT be listed — their 401s should still redirect to login when tokens expire.
  private readonly publicRoutes = [
    '/bootstrap',
    '/login',
    '/tunnel/login',
    '/admin/login',
    '/forgot-password',
    '/resend-email',
    '/reset-password',
    '/docs',
    '/public-stream',
    '/landing-page',
    '/sms-campaign-success-page',
    '/receipt',
  ];

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  /**
   * Check if the current route is a public route
   */
  private isPublicRoute(): boolean {
    const [pathname] = this.router.url.split('?');
    const currentUrl = pathname.split('#')[0];
    if (isFeedbackPublicPath(currentUrl)) {
      return true;
    }
    return this.publicRoutes.some(route => currentUrl.startsWith(route));
  }

  /**
   * Tunnel kiosk uses `tunnelData` + `/tunnel`, not `userData`. Avoid running the
   * regular-user expiration path on 401 (wrong `/login` + wrong storage keys).
   */
  private isTunnelAppRoute(): boolean {
    return isTunnelAppContext(this.router.url);
  }

  private handleTunnelTokenExpiration(): void {
    if (isThisTabImpersonating()) {
      removeImpersonationStorageItem(IMPERSONATION_TUNNEL_DATA_KEY);
      removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
    } else {
      localStorage.removeItem('tunnelData');
    }
    this.router.navigate(['/tunnel/login']);
  }

  /**
   * Handle token expiration - check if user is super admin and redirect accordingly
   */
  private handleTokenExpiration(): void {
    const [pathname] = this.router.url.split('?');
    const currentUrl = pathname.split('#')[0];
    const isAdminRoute = currentUrl.startsWith('/admin/');
    const superAdminData = localStorage.getItem('superAdminData');
    const impersonating = isThisTabImpersonating();

    if (isAdminRoute || (superAdminData && !impersonating)) {
      localStorage.removeItem('superAdminData');
      localStorage.removeItem('userData');
      removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
      this.authService.user.set(null);
      this.router.navigate(['/admin/login']);
      return;
    }

    localStorage.removeItem('userData');
    this.authService.user.set(null);
    this.router.navigate(['/login']);
  }

  /**
   * Intercepts HTTP requests to monitor responses and handle authentication errors (e.g., token expiration) globally.
   * @example
   * ntercept(req, next)
   * Observable<HttpEvent<any>>
   * @param {HttpRequest<any>} req - The outgoing HTTP request to monitor and forward.
   * @param {HttpHandler} next - The next interceptor or backend handler to process the request.
   * @returns {Observable<HttpEvent<any>>} An observable emitting HTTP events for the request.
   **/
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event: HttpEvent<any>) => {
        // Log successful responses
        if (event instanceof HttpResponse) {
          void 0; // Log successful responses if needed
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Log errors

        // Exclude auth-related endpoints from triggering logout on 401
        // (login, forgot-password, reset-password can return 401 for invalid credentials)
        const url = req.url.toLowerCase();
        const isAuthEndpoint =
          url.includes('/user/auth/login') ||
          url.includes('/user/login') ||
          url.includes('/location/login') ||
          url.includes('/user/auth/password/forgot') ||
          url.includes('/user/forgot-password') ||
          url.includes('/user/auth/password/reset') ||
          url.includes('/user/reset-password') ||
          url.includes('/admin/super-admin/login');

        // Tunnel pages handle their own 401 on core location APIs. Optional calls
        // (e.g. user/payment-modes, shop terminal/readers) use shop auth and must
        // not wipe the tunnel session or redirect to login.
        if (
          (error.status === 401 || error.error?.message === 'Invalid or expired token') &&
          !isAuthEndpoint &&
          !this.isPublicRoute() &&
          !this.isTunnelAppRoute()
        ) {
          this.handleTokenExpiration();
        }
        return throwError(() => error);
      })
    );
  }
}
